import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Yoco success landing page. The URL is user-controlled so we NEVER trust it
 * to say "paid". We poll the backend order status which only flips to
 * `paid` once we server-side confirm the checkout with Yoco.
 */
export const YocoPaymentSuccess = () => {
  const [params] = useSearchParams();
  const orderId = params.get('order_id');
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/yoco/order/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        // Poll every 2s up to ~30s while pending — the webhook usually lands within 5s.
        if (data.payment_status === 'pending' && tries < 15) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch (e) {
        if (!cancelled) setOrder({ payment_status: 'error' });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, tries]);

  const status = order?.payment_status;

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center py-16 px-6">
      <div className="max-w-md text-center">
        {!orderId ? (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-6 text-muted" strokeWidth={1.5} />
            <div className="caps text-muted mb-3">Missing order</div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">
              We couldn&apos;t find your <em className="italic">order.</em>
            </h1>
          </>
        ) : status === 'paid' ? (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto mb-6" strokeWidth={1.5} />
            <div className="caps text-muted mb-3" data-testid="yoco-status">Payment confirmed</div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4" data-testid="yoco-success-title">
              Thanks. You&apos;re <em className="italic">paid.</em>
            </h1>
            <p className="text-muted mb-2">Order <span className="font-mono text-ink">{order.order_id}</span></p>
            <p className="text-muted mb-8">R{Number(order.amount_zar).toLocaleString('en-ZA')} — {order.transaction_id}</p>
          </>
        ) : status === 'failed' ? (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-6 text-red-600" strokeWidth={1.5} />
            <div className="caps text-muted mb-3">Payment failed</div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">
              Something went <em className="italic">wrong.</em>
            </h1>
            <p className="text-muted mb-8">The payment for order <span className="font-mono text-ink">{order.order_id}</span> didn&apos;t go through. Please try again.</p>
          </>
        ) : (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin text-muted" strokeWidth={1.5} />
            <div className="caps text-muted mb-3" data-testid="yoco-status">Confirming</div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">
              Confirming your <em className="italic">payment…</em>
            </h1>
            <p className="text-muted mb-8">This usually takes a few seconds. We&apos;re waiting for Yoco to confirm.</p>
          </>
        )}
        <button onClick={() => navigate('/')} className="btn-editorial btn-solid" data-testid="yoco-success-home-btn">
          Back to home →
        </button>
      </div>
    </div>
  );
};
